/**
 * KinesisStreamWriter.
 *
 * writes to AWS Kinesis stream
 * @copyright Realeyes OU. All rights reserved.
 */

import Kinesis from 'aws-sdk/clients/kinesis';
import CredentialProvider from './AwsCredentialProvider';
import Logger from '../Logger';

export default class KinesisStreamWriter {
    /**
     * Creates KinesisStreamWriter instance.
     * @param{string} streamName stream name
     * @param {ConfigurationManager} configurationManager entity to get configuration
     */
    constructor(streamName, configurationManager) {
        this.configurationManager = configurationManager;
        this.config = configurationManager.config;
        this.previousPromise = Promise.resolve();
        this.streamName = streamName;
        this.logger = new Logger(`KSW:${this.streamName}`, configurationManager.isDebugMode());
    }

    /**
     *
     * @param{Object} event which should be writen to kinesis stream
     * @return{Promise.<boolean>}
     */
    write(event) {
        if (!event) {
            this.logger.logError('event is empty');
            return Promise.reject(new Error('event is empty'));
        }
        this.logger.logDebug(`recieved event: ${JSON.stringify(event)}`);
        const recordToSend = {
            Data: JSON.stringify(event),
            PartitionKey: `sid-${event.sid}`,
            StreamName: this.streamName,
            SequenceNumberForOrdering: ((event.sequenceNo || 1) - 1).toString(),
        };
        this.logger.logDebug(`transformed event: ${JSON.stringify(recordToSend)}`);

        const putRecord = (record, retries) => new Promise((resolve, reject) => {
            this.logger.logDebug(`start to put record, retry - ${retries},record ${JSON.stringify(record)}`);
            this.getKinesisService().then((kinesisService) => {
                kinesisService.putRecord(record, (err, data) => {
                    if (err) {
                        this.logger.logError('can not send record to kinesis stream');
                        this.logger.logError(err);
                        if (retries === undefined) {
                            this.logger.logDebug('send record to retry!');
                            this.previousPromise = putRecord(record, 3);
                        } else if (retries > 0) {
                            this.logger.logDebug('send record to retry!');
                            this.previousPromise = putRecord(record, retries - 1);
                        } else {
                            this.logger.logDebug('retries finished!');
                            reject(record, retries || 3, err);
                        }
                    } else {
                        this.logger.logDebug(data);
                        resolve(data);
                    }
                });
            });
        });

        this.logger.logDebug('start to wait previous promise');
        return new Promise((resolve, reject) => {
            this.previousPromise.then(() => {
                this.logger.logDebug('previous promise done');
                this.previousPromise = putRecord(recordToSend);
                resolve(true);
            }, (err) => {
                this.logger.logDebug('previous promise rejected');
                this.logger.logError(err);
                reject(err);
            });
        });
    }

    /**
     *
     * @param{boolean} force is a flag which used to re-create kinesis service
     * initialize kinesis service and return it back
     * @return{Promise.<AWS.Kinesis>}
     */
    getKinesisService(force) {
        this.logger.logDebug(`start to get kinesis service, force-${force}`);
        return new Promise((resolve, reject) => {
            if (!this.kinesis || force || this.creds.isExpired()) {
                new CredentialProvider(this.configurationManager).getCredentials(force).then((creds) => {
                    // todo: implement the sync clock offset logic
                    // AWS.config.update({ systemClockOffset: AbsoluteTime - Date.now() });
                    const initConfig = {
                        region: this.config.aws.kinesis.defaultRegion,
                        credentials: creds.awsCredentials,
                    };

                    this.logger.logDebug(`start to create kinesis service with config ${JSON.stringify(initConfig)}`);
                    this.kinesis = new Kinesis(initConfig);
                    this.creds = creds;
                    this.logger.logDebug('kinesis service created');
                    resolve(this.kinesis);
                }, reject);
            } else {
                this.logger.logDebug('used previous kinesis service');
                resolve(this.kinesis);
            }
        });
    }
}
